import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@shared/components/ui/button";
import { Input } from "@shared/components/ui/input";

import { toast } from "sonner";
import { ReloadIcon } from "@radix-ui/react-icons";

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@shared/components/ui/form";
import { useAuth } from "@features/auth/contexts/AuthContext";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { SelectCity } from "@features/settings/citys/SelectCity";

export default function RegisterForm() {
  const { t } = useTranslation();
  const { register, isRegisterPending, login } = useAuth();
  const navigate = useNavigate();

  // Define the schema for form validation with translated messages
  const formSchema = z.object({
    email: z.string().email(t("validation.invalidEmail")),
    password: z.string().min(8, t("validation.passwordMinLength", { min: 8 })),
    fullName: z.string().min(4, t("validation.fullNameMinLength", { min: 4 })),
    phone: z
      .string()
      .min(10, t("validation.phoneMinLength", { min: 10 }))
      .max(10, t("validation.phoneMaxLength", { max: 10 })),
    brandName: z.string(),
    city: z.string(),
    website: z.string(),
    address: z.string(),
    neighborhood: z.string(),
  });
  // Initialize the form with Zod schema
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: "",
      password: "",
      fullName: "",
      phone: "",
      brandName: "",
      city: "",
      website: "",
      address: "",
      neighborhood: "",
    },
  });

  function onSubmit(values) {
    const toastId = toast.loading(t("messages.loading"));
    register(values, {
      onSuccess: (data) => {
        toast.success(
          t("auth.registerSuccess", { brandName: data.brandName }),
          {
            id: toastId,
          }
        );
        
        // Auto-login after successful registration
        const loginToastId = toast.loading(t("auth.loggingIn"));
        login(
          { identifier: values.email, password: values.password },
          {
            onSuccess: () => {
              toast.success(t("auth.loginSuccess"), { id: loginToastId });
              navigate("/");
            },
            onError: (error) => {
              // If auto-login fails, redirect to login page
              toast.error(t("auth.pleaseLogin"), { id: loginToastId });
              navigate("/login");
            },
          }
        );
      },
      onError: (error) => {
        toast.error(`${error.response.data.message}`, { id: toastId });
      },
    });
  }
  return (
    <>
      <div className="text-center py-10">
        <h1 className="text-3xl font-bold"> {t("auth.registerNewAccount")}</h1>
      </div>
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-2 flex flex-col justify-center"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FormField
              control={form.control}
              name="fullName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.fullName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("auth.enter", {
                        entity: t("user.fullName"),
                      })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="brandName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.brandName")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("auth.enter", {
                        entity: t("user.brandName"),
                      })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.email")}</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder={t("auth.enter", {
                      entity: t("user.email"),
                    })}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItem>
                <FormLabel>{t("user.password")}</FormLabel>
                <FormControl>
                  <Input
                    type="password"
                    placeholder={t("auth.enter", {
                      entity: t("user.password"),
                    })}
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <FormField
              control={form.control}
              name="city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.city")}</FormLabel>
                  <FormControl>
                    <SelectCity
                      // pass the current value as an object expected by the SelectCity component
                      city={field.value ? { name: field.value } : null}
                      // when a city is selected, update the form field with the city name (schema expects a string)
                      onSelectCity={(selectedCity) =>
                        field.onChange(selectedCity?.name || "")
                      }
                      className="w-full"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="neighborhood"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.neighborhood")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("auth.enter", {
                        entity: t("user.neighborhood"),
                      })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <div className="col-span-2">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("user.address")}</FormLabel>
                    <FormControl>
                      <Input
                        placeholder={t("auth.enter", {
                          entity: t("user.address"),
                        })}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.phone")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("auth.enter", {
                        entity: t("user.phone"),
                      })}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="website"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("user.website")}</FormLabel>
                  <FormControl>
                    <Input
                      placeholder={t("users.websitePlaceholder")}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
          <Button type="submit" disabled={isRegisterPending}>
            {isRegisterPending ? (
              <>
                <ReloadIcon className="mr-2 h-4 w-4 animate-spin" />
                {t("main.loading")}
              </>
            ) : (
              t("auth.register")
            )}
          </Button>
        </form>
      </Form>
      <div className="mt-4 text-center text-sm">
        {t("auth.haveAccount")}{" "}
        <Link to="/login" className="underline">
          {t("auth.login")}
        </Link>
      </div>
    </>
  );
}
